import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { servicesData } from '../mock';
import { Milk, Award, Leaf, Stethoscope, Shield, ArrowRight } from 'lucide-react';

const iconMap = {
  Milk: Milk,
  Horse: Award
};

export const ServicesPage = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${require("../images/farm2.jpeg")})` }}
        />
        <div className="absolute inset-0 bg-farm-dark/70" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-6"
        >
          <h1 className="text-6xl md:text-7xl font-serif font-bold text-farm-cream mb-6">
            Our <span className="text-farm-gold">Services</span>
          </h1>
          <p className="text-xl text-farm-cream/80 max-w-2xl mx-auto">
            Premium dairy production and elite horse breeding
          </p>
        </motion.div>
      </section>

      {/* Services Details */}
      <section ref={containerRef} className="py-32 bg-farm-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-32">
          {servicesData.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="relative overflow-hidden rounded-3xl shadow-2xl"
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[500px] object-cover"
                    />
                    <div className="absolute top-6 right-6 w-20 h-20 bg-farm-gold rounded-full flex items-center justify-center shadow-xl">
                      <Icon size={40} className="text-farm-dark" />
                    </div>
                  </motion.div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <h2 className="text-5xl font-serif font-bold text-farm-cream mb-6">
                    {service.title}
                  </h2>
                  <p className="text-xl text-farm-cream/80 leading-relaxed mb-8">
                    {service.description}
                  </p>

                  <div className="space-y-4 mb-8">
                    {service.features.map((feature, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -30 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.5, delay: index * 0.2 + idx * 0.1 }}
                        className="flex items-start gap-4 bg-farm-brown/10 backdrop-blur-sm border border-farm-green/20 rounded-xl p-4 hover:border-farm-gold/50 transition-all duration-300"
                      >
                        <div className="w-2 h-2 bg-farm-gold rounded-full mt-2 flex-shrink-0" />
                        <span className="text-farm-cream/90 text-lg">{feature}</span>
                      </motion.div>
                    ))}
                  </div>

                  {/* <motion.button
                    whileHover={{ scale: 1.05, x: 5 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-farm-gold text-farm-dark rounded-lg font-semibold text-lg flex items-center gap-2 shadow-xl hover:bg-farm-cream transition-colors duration-300"
                  >
                    Learn More
                    <ArrowRight size={20} />
                  </motion.button> */}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-32 bg-gradient-to-b from-farm-dark to-farm-brown/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-serif font-bold text-farm-cream mb-6">
              Additional <span className="text-farm-gold">Offerings</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Leaf,
                title: 'Organic Products',
                description: 'Certified organic dairy products delivered fresh to your door'
              },
              {
                icon: Shield,
                title: 'Consultation',
                description: 'Expert guidance on breeding programs and farm management'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-farm-dark/50 backdrop-blur-sm border border-farm-green/20 rounded-2xl p-8 text-center hover:border-farm-gold/50 transition-all duration-500 shadow-xl hover:shadow-farm-gold/20"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="w-16 h-16 bg-farm-gold rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
                >
                  <item.icon size={32} className="text-farm-dark" />
                </motion.div>
                <h3 className="text-2xl font-serif font-bold text-farm-cream mb-4">
                  {item.title}
                </h3>
                <p className="text-farm-cream/70">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};